'use client'

import { useState, useCallback } from 'react'

import { reloadPage, useWindowEvent } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import {
  addSelectedFilter,
  removeSelectedFilter,
  DynamicFilterModel,
  SelectedDynamicFilterModel,
  DisplayType,
  OptionType,
  SelectionType,
  findFilterOptionById,
} from '@marketplace-web/catalog/catalog-data'
import {
  DynamicFilterReducerAction,
  useCatalogContext,
} from '@marketplace-web/catalog/catalog-page-data'

import { clickEvent } from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'
import { ClickableElement } from 'constants/tracking/clickable-elements'

import GridFilterDropdown from '../common/DynamicFilters/GridFilter/GridFilterDropdown'
import ListFilterDropdown from '../common/DynamicFilters/ListFilter/ListFilterDropdown'
import SearchFilterDropdown from '../common/DynamicFilters/SearchFilter/SearchFilterDropdown'
import useFetchDynamicFilters from '../../../hooks/useFetchDynamicFilters'
import useFetchCatalogBrand from '../../../hooks/useFetchCatalogBrand'
import useFetchDynamicFiltersFacets from '../../../hooks/useFetchDynamicFiltersFacets'
import CatalogFilter from './CatalogFilter'
import PriceFilter from './PriceFilter'
import SortFilter from './SortFilter'

type Props = {
  learnMoreUrl?: string | null
}

const Filters = ({ learnMoreUrl }: Props) => {
  const { track } = useTracking()
  const {
    state: {
      dynamicFilters: { dynamicFilters, selectedDynamicFilters, filterFacetsCount },
    },
    dispatch,
  } = useCatalogContext()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const fetchCatalogBrand = useFetchCatalogBrand()
  const fetchDynamicFiltersFacets = useFetchDynamicFiltersFacets()
  const [totalItemsCount, setTotalItemsCount] = useState<number | null>(0)

  useWindowEvent('popstate', reloadPage)

  const handleItemFacetChange = useCallback(
    (currentSelectedFilters: Array<SelectedDynamicFilterModel>, type: string) => {
      let newTotal = 0

      const filteredSelections = currentSelectedFilters.filter(
        selectedFilter => selectedFilter.type === type,
      )

      if (!filteredSelections.length) {
        dispatch({
          type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS,
          payload: { filterFacetsCount: null },
        })
        setTotalItemsCount(newTotal)

        return
      }

      filteredSelections.forEach(selectedFilter => {
        const matchingFilter = dynamicFilters.find(filter => filter.type === selectedFilter.type)

        if (!matchingFilter || !selectedFilter.ids.length) return

        if (matchingFilter.isAsyncFacet) {
          fetchDynamicFiltersFacets({
            selectedDynamicFilters: currentSelectedFilters,
            filterCode: type,
          })
        } else {
          selectedFilter.ids.forEach(id => {
            const matchedOption = findFilterOptionById(matchingFilter.options, id)
            const itemCount = matchedOption?.itemsCount ?? 0
            newTotal += itemCount
          })
        }
      })

      setTotalItemsCount(newTotal)
    },
    [dispatch, fetchDynamicFiltersFacets, dynamicFilters],
  )

  const handleFilterOpen = (type: string) => {
    // rest filter facet count when entering a filter
    dispatch({
      type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS,
      payload: { filterFacetsCount: null },
    })

    if (type) {
      handleItemFacetChange(selectedDynamicFilters, type)
    }
  }

  const handleFilterUpdate = useLatestCallback(
    (updatedSelectedFilters: Array<SelectedDynamicFilterModel>, type: string) => {
      dispatch({
        type: DynamicFilterReducerAction.SET_SELECTED_DYNAMIC_FILTERS,
        payload: { selectedDynamicFilters: updatedSelectedFilters },
      })

      handleItemFacetChange(updatedSelectedFilters, type)
    },
  )

  const handleAddSelectedFilter = useLatestCallback(
    (newFilter: { type: string; ids: Array<number> }) => {
      const updatedSelectedFilters = addSelectedFilter(newFilter, selectedDynamicFilters)

      handleFilterUpdate(updatedSelectedFilters, newFilter.type)
    },
  )

  const handleRemoveSelectedFilter = useLatestCallback(
    (removeFilter: { type: string; id: number }) => {
      const updatedSelectedFilters = removeSelectedFilter(removeFilter, selectedDynamicFilters)

      handleFilterUpdate(updatedSelectedFilters, removeFilter.type)
    },
  )

  const handleDynamicFilterToggle = useLatestCallback(
    (id: number, type: string, selectionType: SelectionType, selectedIds: Array<number>) => {
      if (selectedIds.includes(id)) {
        if (selectionType === SelectionType.Multi) {
          handleRemoveSelectedFilter({ type, id })
        }
      } else {
        const newIds = selectionType === SelectionType.Single ? [id] : [...selectedIds, id]
        handleAddSelectedFilter({ type, ids: newIds })
      }
    },
  )

  const handleSubmit = () => {
    fetchDynamicFilters({ selectedDynamicFilters })
    fetchCatalogBrand({ selectedDynamicFilters })

    track(
      clickEvent({
        target: ClickableElement.ApplyFilters,
        screen: Screen.Catalog,
      }),
    )
  }

  const getRootOptionType = (dynamicFilter: DynamicFilterModel) => {
    return dynamicFilter.options.find(option => !!option.type)?.type || OptionType.Default
  }

  const getSelectedFilter = (type: string) => {
    return selectedDynamicFilters.find(filter => filter.type === type)
  }

  const renderDynamicFilter = (filter: DynamicFilterModel) => {
    const selectedDynamicFilter = getSelectedFilter(filter.type)
    const key = `${filter.type}-${filter.id}`
    let totalFacetsCount: number | null | undefined

    if (filter.isAsyncFacet) {
      totalFacetsCount = filterFacetsCount ?? undefined
    } else {
      totalFacetsCount = totalItemsCount === 0 ? null : totalItemsCount
    }

    switch (filter.displayType) {
      case DisplayType.List: {
        return (
          <ListFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isHidden={filter.isHidden}
            experiments={filter.experiments}
            links={filter.links}
            totalItemsCount={totalFacetsCount}
            totalCountMax={filter.totalCountMax}
            isAsyncFacet={filter.isAsyncFacet}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            fromScreen={Screen.Catalog}
            selectedIds={selectedDynamicFilter?.ids || []}
            onSubmit={handleSubmit}
            onFilterOpen={handleFilterOpen}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.ListSearch: {
        return (
          <SearchFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isHidden={filter.isHidden}
            experiments={filter.experiments}
            links={filter.links}
            totalItemsCount={totalFacetsCount}
            totalCountMax={filter.totalCountMax}
            isAsyncFacet={filter.isAsyncFacet}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            fromScreen={Screen.Catalog}
            translations={filter.searchTranslations}
            selectedIds={selectedDynamicFilter?.ids || []}
            onSubmit={handleSubmit}
            onFilterOpen={handleFilterOpen}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.Grid: {
        return (
          <GridFilterDropdown
            key={key}
            type={filter.type}
            title={filter.title}
            fromScreen={Screen.Catalog}
            isSelectionHighlighted={filter.isSelectionHighlighted || false}
            isHidden={filter.isHidden}
            experiments={filter.experiments}
            rootOptionType={getRootOptionType(filter)}
            options={filter.options}
            selectedIds={selectedDynamicFilter?.ids || []}
            onListOptionSelect={id =>
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                selectedDynamicFilter?.ids || [],
              )
            }
          />
        )
      }
      case DisplayType.HybridPrice: {
        return <PriceFilter key={key} minimumValidation={filter.minimumValidation} />
      }
      default:
        return null
    }
  }

  const renderDynamicFilters = () => {
    return dynamicFilters.map(filter => renderDynamicFilter(filter))
  }

  return (
    <div className="u-flexbox u-flex-wrap">
      <CatalogFilter />
      {renderDynamicFilters()}
      <SortFilter learnMoreUrl={learnMoreUrl} />
    </div>
  )
}

export default Filters
